import { useEffect, useState, FormEvent } from "react";
import { api } from "../api/client.js";
import { useAuth } from "../context/AuthContext.js";

interface BudgetScope { id: string; name: string; cap_microeur: string; reserved_microeur: string; committed_microeur: string; revision: number; }

function microeur(v: string | null | undefined) {
  if (!v) return "€0.00";
  return `€${(Number(v) / 1_000_000).toFixed(4)}`;
}

export function CostsPage() {
  const { role } = useAuth();
  const [scopes, setScopes] = useState<BudgetScope[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", cap_microeur: "10000000" });
  const [editId, setEditId] = useState<string | null>(null);
  const [editCap, setEditCap] = useState("");
  const [formError, setFormError] = useState("");

  function load() {
    setLoading(true);
    api.get<{ scopes: BudgetScope[] }>("/budget-scopes")
      .then(r => setScopes(r.scopes ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setFormError("");
    try {
      await api.post("/budget-scopes", form);
      setCreating(false);
      setForm({ name: "", cap_microeur: "10000000" });
      load();
    } catch (err: unknown) {
      const e = err as { data?: { message?: string } };
      setFormError(e.data?.message || "Error al crear");
    }
  }

  async function handleUpdateCap(scope: BudgetScope) {
    try {
      await api.patch(`/budget-scopes/${scope.id}`, {
        cap_microeur: editCap,
        _revision: scope.revision,
      });
      setEditId(null);
      load();
    } catch (err: unknown) {
      const e = err as { data?: { message?: string } };
      alert(e.data?.message || "Error al actualizar");
    }
  }

  const total = scopes.reduce((acc, s) => acc + Number(s.cap_microeur || 0), 0);
  const reserved = scopes.reduce((acc, s) => acc + Number(s.reserved_microeur || 0), 0);
  const committed = scopes.reduce((acc, s) => acc + Number(s.committed_microeur || 0), 0);

  if (loading) return <div style={{ padding: 24, color: "#888" }}>Cargando…</div>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Costes y presupuesto</h1>
          <p style={{ color: "#666", fontSize: 13, margin: "4px 0 0" }}>Límites, reservas y gasto por ámbito</p>
        </div>
        {role === "owner" && (
          <button onClick={() => setCreating(!creating)} style={btnPrim}>+ Nuevo ámbito</button>
        )}
      </div>

      {/* Resumen global */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
        {[
          { label: "Cap. total", val: microeur(String(total)), color: "#2d6a4f" },
          { label: "Reservado", val: microeur(String(reserved)), color: "#d97706" },
          { label: "Comprometido", val: microeur(String(committed)), color: "#dc2626" },
          { label: "Disponible", val: microeur(String(Math.max(0, total - reserved - committed))), color: "#1d4ed8" },
        ].map(c => (
          <div key={c.label} style={{ background: "#fff", borderRadius: 10, padding: "16px 20px", flex: "1 1 180px", borderLeft: `3px solid ${c.color}` }}>
            <div style={{ fontSize: 11, color: "#888" }}>{c.label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#1a1a1a", marginTop: 2 }}>{c.val}</div>
          </div>
        ))}
      </div>

      {creating && role === "owner" && (
        <form onSubmit={handleCreate} style={{ background: "#fff", padding: 20, borderRadius: 10, marginBottom: 20, display: "flex", flexDirection: "column", gap: 10, maxWidth: 400 }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>Nuevo ámbito de presupuesto</h3>
          {formError && <div style={{ color: "#dc2626", fontSize: 13 }}>{formError}</div>}
          <label style={lbl}>Nombre <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required style={inp} /></label>
          <label style={lbl}>Cap. (microEUR) <input type="number" value={form.cap_microeur} onChange={e => setForm({ ...form, cap_microeur: e.target.value })} required style={inp} placeholder="10000000 = €10" /></label>
          <div style={{ background: "#f8f6f0", borderRadius: 6, padding: "6px 10px", fontSize: 11, color: "#888" }}>
            1 EUR = 1,000,000 microEUR
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="submit" style={btnPrim}>Crear</button>
            <button type="button" onClick={() => setCreating(false)} style={btnSec}>Cancelar</button>
          </div>
        </form>
      )}

      {scopes.length === 0 ? (
        <div style={{ background: "#fff", borderRadius: 10, padding: 40, textAlign: "center", color: "#888" }}>
          Sin ámbitos de presupuesto. {role === "owner" && "Crea uno para controlar el gasto en generación de imágenes y vídeo."}
        </div>
      ) : (
        <div style={{ background: "#fff", borderRadius: 10, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #f0ece4" }}>
                {["Ámbito", "Cap.", "Reservado", "Comprometido", "Disponible", ...(role === "owner" ? [""] : [])].map(h => (
                  <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 12, color: "#888", fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {scopes.map(s => {
                const avail = Number(s.cap_microeur) - Number(s.reserved_microeur) - Number(s.committed_microeur);
                return (
                  <tr key={s.id} style={{ borderBottom: "1px solid #f8f6f0" }}>
                    <td style={{ padding: "12px 16px", fontWeight: 500, fontSize: 14 }}>{s.name}</td>
                    <td style={{ padding: "12px 16px", fontSize: 13 }}>
                      {editId === s.id ? (
                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          <input type="number" value={editCap} onChange={e => setEditCap(e.target.value)} style={{ ...inp, width: 120 }} />
                          <button onClick={() => handleUpdateCap(s)} style={btnMini}>✓</button>
                          <button onClick={() => setEditId(null)} style={btnMiniSec}>✕</button>
                        </div>
                      ) : microeur(s.cap_microeur)}
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: 13, color: "#d97706" }}>{microeur(s.reserved_microeur)}</td>
                    <td style={{ padding: "12px 16px", fontSize: 13, color: "#dc2626" }}>{microeur(s.committed_microeur)}</td>
                    <td style={{ padding: "12px 16px", fontSize: 13, color: avail < 0 ? "#dc2626" : "#2d6a4f", fontWeight: 600 }}>{microeur(String(Math.max(0, avail)))}</td>
                    {role === "owner" && (
                      <td style={{ padding: "12px 16px" }}>
                        <button onClick={() => { setEditId(s.id); setEditCap(s.cap_microeur); }} style={btnMiniSec}>Editar cap</button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const btnPrim: React.CSSProperties = { padding: "8px 16px", background: "#2d6a4f", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 14 };
const btnSec: React.CSSProperties = { padding: "8px 16px", border: "1px solid #ddd", borderRadius: 6, cursor: "pointer", background: "none", fontSize: 13 };
const btnMini: React.CSSProperties = { padding: "3px 8px", background: "#2d6a4f", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12 };
const btnMiniSec: React.CSSProperties = { padding: "3px 8px", background: "#f3f4f6", border: "1px solid #ddd", borderRadius: 4, cursor: "pointer", fontSize: 12 };
const inp: React.CSSProperties = { padding: "7px 10px", border: "1px solid #ddd", borderRadius: 6, fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" };
const lbl: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 4, fontSize: 13, color: "#444" };
